import {appendQueryObjectToURL} from '@netx/core-next/utils/url';
import {spawnNewWindow} from '@netx/core-next/utils/browser';
import {modalManagerStore} from '@netx/core-components/ModalManager';
import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {isPublicUser} from '@netx/core-user-next/utils/isPublicUser';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {triggerCustomAction, useReactQueryGetCustomActionMenu} from '../api';
import {CUSTOM_ACTION_EVENT} from '../actions';
import {CustomActionPayloadType} from '../constants';
import {CUSTOM_ACTION_MESSAGE} from '../locales';
import type {UserCustomAction, CustomActionMenus} from '../types';
import {CustomActionBlockingDialog} from '../components/CustomActionBlockingDialog';

const DEFAULT_DATA: CustomActionMenus = {
	selection: [] as UserCustomAction[],
};

/**
 * Custom action menus for user
 */
export const useCustomActionMenus = () => {
	const canUseCustomActions = !isPublicUser();

	const query = useReactQueryGetCustomActionMenu(undefined, {
		enabled: canUseCustomActions,
		refetchOnWindowFocus: false,
	});

	// refetch when the actions update
	useDispatcher(CUSTOM_ACTION_EVENT.UPDATED, () => {
		query.refetch();
	});

	/**
	 * Execute custom action
	 * @param customAction
	 */
	const executeCustomAction = useConstCallback(
		(customAction: UserCustomAction) => {
			if (!canUseCustomActions) return;

			modalManagerStore
				.show('CustomActionBlockingDialog', CustomActionBlockingDialog, {
					userCustomAction: customAction,
					blocking: true,
				})
				.then(() => {
					triggerCustomAction({
						id: customAction.customActionId,
						data: {
							payload: CustomActionPayloadType.CURRENT_SELECTION,
						},
					})
						.then((eventId) => {
							modalManagerStore.show(
								'CustomActionBlockingDialog',
								CustomActionBlockingDialog,
								{
									successMessage: CUSTOM_ACTION_MESSAGE.SUCCEEDED,
									userCustomAction: customAction,
									blocking: false,
								},
							);
							if (!customAction.targetUrl) return;
							spawnNewWindow(
								appendQueryObjectToURL(customAction.targetUrl, {
									eventId,
								}),
							);
						})
						.catch((ex) => {
							modalManagerStore.show(
								'CustomActionBlockingDialog',
								CustomActionBlockingDialog,
								{
									errorMessage: CUSTOM_ACTION_MESSAGE.FAILED,
									userCustomAction: customAction,
									blocking: false,
								},
							);
						});
				});
		},
	);

	return {
		data: canUseCustomActions ? query.data : DEFAULT_DATA,
		loading: canUseCustomActions && (query.isLoading || query.isFetching),
		executeCustomAction,
	};
};

export default useCustomActionMenus;
